import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper, 
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  LocationOn as LocationIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import config from '../config';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [tipoAutenticacion, setTipoAutenticacion] = useState(3); // Por defecto correo
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codigoEnviado, setCodigoEnviado] = useState(false);
  
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Configuración de habilitación por tipo de auth
  const authConfig = {
    password: false,    // Cambiar a true cuando quieras habilitar
    sms: false,         // Cambiar a true cuando quieras habilitar  
    email: true         // Actualmente habilitado
  };

  const handleSendCode = async () => {
    if (!username) {
      setError('Por favor ingrese su correo electrónico');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = `${config.API_BASE_URL}/api/usuarios/send-code`;
      console.log('📧 PWA send-code URL:', url);
      console.log('📧 PWA send-code payload:', { username, tipo_autenticacion: tipoAutenticacion });

      const response = await axios.post(url, {
        username,
        tipo_autenticacion: tipoAutenticacion
      });

      console.log('📧 PWA send-code respuesta:', response.status, response.data);
      setCodigoEnviado(true);
      setError('');
    } catch (error) {
      console.error('📧 PWA send-code error:', error.message);
      console.error('📧 PWA send-code status:', error.response?.status);
      console.error('📧 PWA send-code data:', error.response?.data);
      console.error('📧 PWA send-code config URL:', error.config?.url);
      setError(error.response?.data?.error || 'Error al enviar el código. Verifique su correo.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
  
    try {
      const loginData = {
        username,
        tipo_autenticacion: tipoAutenticacion
      };

      // Solo agregar password si es auth por contraseña
      if (tipoAutenticacion === 1) {
        loginData.password = password;
      }

      // Solo agregar codigo si es SMS o correo
      if (tipoAutenticacion === 2 || tipoAutenticacion === 3) {
        loginData.codigo = codigo;
      }

      const response = await axios.post(`${config.API_BASE_URL}/api/usuarios/login`, loginData);
      
      const token = response.data.token;
      const user = JSON.parse(atob(token.split('.')[1]));

      // Nivel 4 (trabajador) solo puede usar la APP móvil
      if (user.nivel >= 4) {
        setError('Tu cuenta es de trabajador de campo. Por favor usa la app móvil TRACKEA para acceder.');
        return;
      }

      localStorage.setItem('token', token);
      login({ ...user, token });

      if (user.nivel === 1) {
        navigate('/admin');
      } else {
        navigate('/home');
      }
    } catch (error) {
      setError('Credenciales incorrectas o cuenta inactiva');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getAuthOptions = () => {
    const options = [];
    if (authConfig.password) options.push({ value: 1, label: 'Contraseña' });
    if (authConfig.sms) options.push({ value: 2, label: 'Código SMS' });
    if (authConfig.email) options.push({ value: 3, label: 'Código por Correo' });
    return options;
  };

  const authOptions = getAuthOptions();

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 50%, #0288D1 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2
    }}>
      <Container maxWidth="sm">
        <Paper 
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header con Logo */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1A237E 0%, #283593 50%, #3949AB 100%)',
            color: 'white',
            padding: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(79, 195, 247, 0.2)',
              opacity: 0.6
            }} />
            
            <Box sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(41, 182, 246, 0.3)',
              opacity: 0.4
            }} />

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              position: 'relative',
              zIndex: 1
            }}>
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4FC3F7, #29B6F6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 8px 20px rgba(79, 195, 247, 0.4)'
              }}>
                <LocationIcon sx={{ fontSize: 35, color: 'white' }} />
              </Box>
            </Box>

            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              sx={{ 
                fontWeight: 'bold',
                mb: 1,
                position: 'relative',
                zIndex: 1
              }}
            >
              TRACKEA
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                opacity: 0.9,
                letterSpacing: 2,
                textTransform: 'uppercase',
                fontSize: '0.9rem',
                position: 'relative',
                zIndex: 1
              }}
            >
              Servicios Digitales
            </Typography>
          </Box>

          {/* Formulario */}
          <Box sx={{ padding: 4 }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                textAlign: 'center', 
                color: '#1A237E',
                fontWeight: 600,
                mb: 3
              }}
            >
              Iniciar Sesión
            </Typography>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              {/* Selector de tipo de autenticación - Solo se muestra si hay más de una opción */}
              {authOptions.length > 1 && (
                <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                  <InputLabel>Tipo de Autenticación</InputLabel>
                  <Select
                    value={tipoAutenticacion}
                    label="Tipo de Autenticación"
                    onChange={(e) => {
                      setTipoAutenticacion(e.target.value);
                      setCodigoEnviado(false);
                      setCodigo('');
                      setPassword('');
                    }}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {authOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Campo de usuario (correo o teléfono) */}
              <TextField
                label={tipoAutenticacion === 2 ? "Número de Teléfono" : "Correo Electrónico"}
                variant="outlined"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                type={tipoAutenticacion === 3 ? "email" : "text"}
                autoComplete={tipoAutenticacion === 3 ? "email" : "tel"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {tipoAutenticacion === 2 ? 
                        <SmsIcon sx={{ color: '#4FC3F7' }} /> : 
                        <EmailIcon sx={{ color: '#4FC3F7' }} />
                      }
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4FC3F7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1A237E',
                    },
                  },
                }}
              />

              {/* Campo de contraseña - Solo si está habilitado */}
              {tipoAutenticacion === 1 && authConfig.password && (
                <TextField
                  label="Contraseña"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#4FC3F7' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#4FC3F7',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1A237E',
                      },
                    },
                  }}
                />
              )}

              {/* Botón para enviar código - Solo para SMS y Email */}
              {(tipoAutenticacion === 2 || tipoAutenticacion === 3) && !codigoEnviado && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleSendCode}
                  disabled={loading || !username}
                  sx={{
                    mb: 2,
                    height: 45,
                    borderRadius: 2,
                    borderColor: '#4FC3F7',
                    color: '#1A237E',
                    '&:hover': {
                      borderColor: '#1A237E',
                      backgroundColor: 'rgba(26, 35, 126, 0.04)'
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    `Enviar código ${tipoAutenticacion === 2 ? 'por SMS' : 'por correo'}`
                  )}
                </Button>
              )}

              {/* Campo de código - Solo si ya se envió el código */}
              {(tipoAutenticacion === 2 || tipoAutenticacion === 3) && codigoEnviado && (
                <TextField
                  label="Código de verificación"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#4FC3F7',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1A237E',
                      },
                    },
                  }}
                />
              )}

              {/* Botón de login */}
              <Button 
                variant="contained" 
                fullWidth 
                type="submit" 
                disabled={loading || (
                  (tipoAutenticacion === 1 && (!password || !username)) ||
                  ((tipoAutenticacion === 2 || tipoAutenticacion === 3) && (!codigoEnviado || !codigo))
                )}
                sx={{
                  height: 50,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1A237E 0%, #283593 50%, #3949AB 100%)',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(26, 35, 126, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 50%, #42A5F5 100%)',
                    boxShadow: '0 12px 25px rgba(26, 35, 126, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: '#E0E0E0',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Ingresar al Sistema'
                )}
              </Button>
            </form>

            <Box sx={{ 
              textAlign: 'center', 
              mt: 3,
              pt: 2,
              borderTop: '1px solid rgba(26, 35, 126, 0.1)'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666',
                  fontSize: '0.8rem'
                }}
              >
                © 2024 TRACKEA - Servicios Digitales
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;